import React from 'react';
import { createPortal } from 'react-dom';

// Portals a dropdown panel straight to document.body, positioned by the
// trigger's live bounding rect — escapes any ancestor's `overflow-x-auto`
// (every ArrayEditor table has one), which otherwise clips or visually
// squashes the panel into an unreadable sliver when a combo/select box sits
// inside a scrollable table cell. `panelRef` is exposed so the caller's
// outside-click handler can recognize clicks landing in the (portaled, so
// DOM-detached from the trigger) panel as "still inside."
function useFloatingRect(open, anchorRef) {
  const [rect, setRect] = React.useState(null);
  React.useLayoutEffect(() => {
    if (!open || !anchorRef.current) { setRect(null); return undefined; }
    const update = () => setRect(anchorRef.current.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => { window.removeEventListener('scroll', update, true); window.removeEventListener('resize', update); };
  }, [open, anchorRef]);
  return rect;
}

function FloatingPanel({ open, anchorRef, panelRef, maxHeight = 240, width, align = 'left', children }) {
  const rect = useFloatingRect(open, anchorRef);
  if (!open || !rect) return null;
  const panelWidth = width || rect.width;
  const left = align === 'right' ? rect.right - panelWidth : rect.left;
  return createPortal(
    <div ref={panelRef}
      style={{ position: 'fixed', top: rect.bottom + 6, left, width: panelWidth, zIndex: 1000, maxHeight }}
      className="rounded-xl border-2 border-[#ff5200]/25 dark:border-orange-500/40 bg-white dark:bg-slate-800 shadow-2xl overflow-y-auto cx-hide-scrollbar py-1.5">
      {children}
    </div>,
    document.body
  );
}

// Small grey question-mark trigger — hover reveals a short explanatory
// tooltip. Portaled via FloatingPanel so it's never clipped inside a table
// cell or cramped grid, same as the dropdowns. Purely explanatory — never
// the only place a piece of information lives.
export function InfoTooltip({ text }) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  return (
    <span ref={anchorRef} className="relative inline-flex align-middle"
      onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" tabIndex={-1}
        className="w-4 h-4 rounded-full bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-300 flex items-center justify-center hover:bg-orange-500/20 hover:text-[#ff5200] transition-colors cursor-help">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>
      <FloatingPanel open={open} anchorRef={anchorRef} width={260} maxHeight={200} align="right">
        <div className="px-3.5 py-2.5 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">{text}</div>
      </FloatingPanel>
    </span>
  );
}

// `tooltip` renders a question-mark hint and `action` an extra small icon
// button (e.g. a "pick these for me" helper) — both flush to the right edge
// of the field, same width as the input below it — rather than crowding the
// label text itself. `action` sits left of `tooltip` when both are given.
export function Field({ label, children, hint, required, tooltip, action }) {
  return (
    <label className="block mb-4">
      <span className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] font-bold tracking-wide uppercase text-slate-500 dark:text-slate-400">
          {label}{required && <span className="text-[#ff5200] ml-0.5">*</span>}
        </span>
        <span className="flex items-center gap-1.5">
          {action}
          {tooltip && <InfoTooltip text={tooltip} />}
        </span>
      </span>
      {children}
      {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

// The signature "orange glow" focus state — soft ring + border tint, no
// harsh outline. This is the house input style; every field in the module
// gets it, not just one form. Tailwind's `ring` utility (CSS-variable-based
// box-shadow) doesn't resolve reliably in this build, so the actual glow is
// a plain CSS rule keyed off `.cx-input:focus` (see CX_INPUT_STYLE below,
// injected once by DPRWorkspace.js) — the Tailwind classes here just carry
// the border-color change, which does work.
const inputClass = "cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-[#ff5200] transition-shadow";

// Injected once (DPRWorkspace.js renders it at the module root, covering
// both the dashboard and every CaseDetail step) — plain CSS, no Tailwind
// ring/shadow indirection, so it's immune to the JIT issue above.
export const CX_INPUT_STYLE = `
  .cx-input:focus { box-shadow: 0 0 0 3px rgba(255,82,0,0.12) !important; }
  .dark .cx-input:focus { box-shadow: 0 0 0 3px rgba(255,82,0,0.22) !important; }
  .cx-input:hover:not(:focus):not(:disabled) { border-color: rgba(255,82,0,0.35); }
  input[type=number].cx-input::-webkit-inner-spin-button,
  input[type=number].cx-input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
  input[type=number].cx-input { -moz-appearance: textfield; }
  .cx-hide-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
  .cx-hide-scrollbar::-webkit-scrollbar { display: none; width: 0; height: 0; }
`;

// Plain numeric editing while focused (so the user types raw digits without
// fighting a formatter), Indian lakhs/crores grouping while blurred (so the
// printed report and the input look the same at a glance). The underlying
// value in state is always a raw number — only the display string changes.
export function NumberInput({ value, onChange, placeholder, step = 'any' }) {
  const [focused, setFocused] = React.useState(false);
  const display = focused || value === undefined || value === null
    ? (value === undefined || value === null ? '' : value)
    : Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return (
    <input
      type={focused ? 'number' : 'text'} step={step} placeholder={placeholder}
      className={inputClass}
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
    />
  );
}

export function TextInput({ value, onChange, placeholder }) {
  return (
    <input type="text" placeholder={placeholder} className={inputClass}
      value={value || ''} onChange={e => onChange(e.target.value)} />
  );
}

// Fully custom listbox — replaces the native <select>. A native dropdown's
// OPEN PANEL is OS-themed and can't be restyled (square corners, the
// platform's own blue selection highlight) no matter how the closed control
// looks, so this renders its own popover instead: rounded panel, orange
// highlight on hover/selected, same house style as every other input.
export function SelectInput({ value, onChange, options }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const panelRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const selected = options.find(o => o.value === value);

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`${inputClass} flex items-center justify-between text-left cursor-pointer ${open ? 'border-[#ff5200] shadow-[0_0_0_3px_rgba(255,82,0,0.12)]' : ''}`}>
        <span className={`truncate min-w-0 ${selected?.label ? '' : 'text-slate-400'}`}>{selected?.label || 'Select…'}</span>
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
          className={`flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180 text-[#ff5200]' : 'text-slate-400'}`}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
        </svg>
      </button>
      <FloatingPanel open={open} anchorRef={wrapRef} panelRef={panelRef}>
        {options.map(o => (
          <button key={o.value} type="button"
            onClick={() => { onChange(o.value); setOpen(false); }}
            className={`w-full text-left px-3.5 py-2 mx-1.5 my-0.5 rounded-lg text-sm transition-colors ${o.value === value
              ? 'bg-orange-500/10 text-[#ff5200] font-semibold'
              : 'text-slate-700 dark:text-slate-200 hover:bg-orange-500/10 hover:text-[#ff5200]'}`}
            style={{ width: 'calc(100% - 12px)' }}>
            {o.label}
          </button>
        ))}
      </FloatingPanel>
    </div>
  );
}

// Multi-select checklist dropdown — e.g. a Product/By-Product mapping to one
// or more Raw Material rows (a bottle draws from PET Granule AND Cap; Rice
// Bran Oil draws from Rice Bran alone). `value` is an array of strings.
export function MultiSelectInput({ value, onChange, options = [] }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const selected = value || [];

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggle = (name) => {
    const next = selected.includes(name) ? selected.filter(n => n !== name) : [...selected, name];
    onChange(next);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)}
        className={`${inputClass} flex items-center justify-between text-left cursor-pointer ${open ? 'border-[#ff5200] shadow-[0_0_0_3px_rgba(255,82,0,0.12)]' : ''}`}>
        <span className={`truncate min-w-0 ${selected.length ? '' : 'text-slate-400'}`}>{selected.length ? selected.join(', ') : 'Select…'}</span>
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"
          className={`flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180 text-[#ff5200]' : 'text-slate-400'}`}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
        </svg>
      </button>
      <FloatingPanel open={open} anchorRef={wrapRef} panelRef={panelRef}>
        {options.length === 0 && <div className="px-3.5 py-2 text-sm text-slate-400">Add a Raw Material row first</div>}
        {options.map(name => (
          <button key={name} type="button"
            onClick={() => toggle(name)}
            className={`w-full flex items-center gap-2 text-left px-3.5 py-2 mx-1.5 my-0.5 rounded-lg text-sm transition-colors ${selected.includes(name)
              ? 'bg-orange-500/10 text-[#ff5200] font-semibold'
              : 'text-slate-700 dark:text-slate-200 hover:bg-orange-500/10 hover:text-[#ff5200]'}`}
            style={{ width: 'calc(100% - 12px)' }}>
            <span className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center ${selected.includes(name) ? 'bg-[#ff5200] border-[#ff5200]' : 'border-slate-300 dark:border-slate-600'}`}>
              {selected.includes(name) && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
            </span>
            {name}
          </button>
        ))}
      </FloatingPanel>
    </div>
  );
}

// Searchable + freely-typeable "combobox" — types a custom value OR picks
// from a styled suggestion panel. Replaces an earlier native <datalist>-backed
// version: the browser's own datalist popup can't be themed (looks like a
// stray OS tooltip, inconsistent across browsers) — this renders its own
// panel in the house style instead, so it looks the same everywhere and
// matches every other input in the module.
export function ComboInput({ value, onChange, options = [], placeholder = 'Type a custom name, or pick a suggestion…' }) {
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);
  const panelRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const query = (value || '').toLowerCase();
  // Word-start matching, not "anywhere in the string" — a plain substring
  // match let typing "MT" pull in "Rmt / m" (it contains "mt" mid-word),
  // a false-positive suggestion nobody wants. Matches only when the query
  // starts the whole option, or starts one of its space/slash-separated
  // words (so "Rmt" still matches typing "R" or "Rmt", just not "mt").
  const filtered = options.filter(o => {
    if (!query) return true;
    const lower = o.toLowerCase();
    return lower.startsWith(query) || lower.split(/[\s/]+/).some(word => word.startsWith(query));
  });

  return (
    <div ref={wrapRef} className="relative">
      <input
        className={`${inputClass} pr-8 ${open ? 'border-[#ff5200]' : ''}`} placeholder={placeholder}
        value={value || ''}
        onFocus={() => setOpen(true)}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
      />
      <button type="button" tabIndex={-1} onClick={() => setOpen(o => !o)}
        className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 transition-transform ${open ? 'text-[#ff5200] rotate-180' : 'text-slate-400 hover:text-[#ff5200]'}`}>
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" /></svg>
      </button>
      {filtered.length > 0 && (
        <FloatingPanel open={open} anchorRef={wrapRef} panelRef={panelRef} maxHeight={208}>
          {filtered.map(o => (
            <button key={o} type="button"
              onClick={() => { onChange(o); setOpen(false); }}
              className="w-full text-left px-3.5 py-2 mx-1.5 my-0.5 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-orange-500/10 hover:text-[#ff5200] transition-colors"
              style={{ width: 'calc(100% - 12px)' }}>
              {o}
            </button>
          ))}
        </FloatingPanel>
      )}
    </div>
  );
}

// Stylish "+ Add" trigger — orange gradient pill with a plus-in-circle icon,
// white text, subtle lift on hover. Used for every "add a row/item/source"
// action across the module (ArrayEditor, StringListEditor, FinancialGrid)
// so they read as one consistent, deliberate control instead of a plain text
// link that's easy to miss.
export function AddButton({ children, onClick, className = '' }) {
  return (
    <button type="button" onClick={onClick}
      style={{ background: `linear-gradient(135deg, ${BRAND.orange}, ${BRAND.orangeHover})`, boxShadow: '0 2px 8px rgba(255,82,0,0.25)' }}
      className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-white text-xs font-bold hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all ${className}`}>
      <span className="w-4 h-4 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
      </span>
      {children}
    </button>
  );
}

// Same modal chrome as the New Project modal (DPRWorkspace.js) — navy-tinted
// blurred backdrop, rounded card, fade/scale-in — reused here for any "fill
// in a proper structured form" flow (e.g. adding a Promoter/Director) that a
// single crammed table row can't do justice to.
const MODAL_ANIM = `@keyframes cxModalIn { from { opacity: 0; transform: translateY(12px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }`;

export function Modal({ open, onClose, title, subtitle, children, maxWidth = '640px' }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,18,84,0.45)', backdropFilter: 'blur(4px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <style>{MODAL_ANIM}</style>
      <div onClick={e => e.stopPropagation()}
        className="w-full bg-white dark:bg-slate-800 rounded-[20px] p-[30px] shadow-2xl max-h-[88vh] overflow-y-auto cx-hide-scrollbar"
        style={{ maxWidth, animation: 'cxModalIn 0.2s cubic-bezier(0.2,0.6,0.3,1)' }}>
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg font-extrabold text-[#001254] dark:text-slate-100">{title}</h3>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0 -mt-1 -mr-1 p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// A labeled section inside a Modal — orange kicker + underline, groups
// related fields (Personal Details, Identification, Contact & Address, ...).
export function ModalSection({ title, children }) {
  return (
    <div className="mb-5">
      <div className="text-[11px] font-extrabold text-[#ff5200] uppercase tracking-wider mb-2.5 pb-1.5 border-b border-orange-500/15">{title}</div>
      {children}
    </div>
  );
}

export function Section({ title, subtitle, children, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="mb-3 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden bg-white dark:bg-slate-800 shadow-sm">
      <button type="button" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3.5 bg-slate-50/60 dark:bg-slate-900/40 hover:bg-slate-100 dark:hover:bg-slate-800 text-left transition-colors">
        <div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-100">{title}</div>
          {subtitle && <div className="text-xs text-slate-400">{subtitle}</div>}
        </div>
        <span className="text-slate-400 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="p-5">{children}</div>}
    </div>
  );
}

// Editable table of repeatable rows (e.g. RM recipe lines, labour rows).
// `nameCatalog` swaps the first column to a searchable ComboInput.
export function ArrayEditor({ rows, columns, onChange, addLabel = '+ Add Item', nameCatalog, lockedCells, nonRemovableIndex, extraActions }) {
  const update = (idx, key, val) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const add = () => onChange([...(rows || []), {}]);

  const numericCols = columns.filter(c => c.type === 'number');
  const computedCols = columns.filter(c => c.type === 'computed');
  // Only worth showing once there's actually something to add up — a single
  // row's "total" is just that row's own numbers restated. A `computed`
  // column (e.g. Per Annum = Salary × 12 × Count) is a genuinely new derived
  // figure though, so its total is worth showing even for a single row.
  const showTotals = ((rows || []).length > 1 && numericCols.length > 0) || ((rows || []).length > 0 && computedCols.length > 0);
  const colTotal = (key) => (rows || []).reduce((s, r) => s + (Number(r[key]) || 0), 0);
  const computedColTotal = (c) => (rows || []).reduce((s, r) => s + (Number(c.compute(r)) || 0), 0);

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900">
              {columns.map(c => <th key={c.key} style={c.width ? { width: c.width } : undefined} className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{c.label}</th>)}
              <th className="border-b border-slate-200 dark:border-slate-700"></th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((row, idx) => (
              <tr key={idx} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/30">
                {columns.map((c, ci) => {
                  const locked = lockedCells && lockedCells(row, idx, c.key);
                  return (
                    <td key={c.key} style={c.width ? { width: c.width } : undefined} className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                      {c.type === 'computed'
                        ? <div className="px-1 py-2 text-slate-600 dark:text-slate-300 font-semibold">{fmt(c.compute(row))}</div>
                        : locked
                        ? <div className={`${inputClass} bg-slate-50 dark:bg-slate-900/60 text-slate-500 dark:text-slate-400 cursor-not-allowed flex items-center`} title="Locked — matches the Production Capacity tab">{row[c.key] ?? ''}</div>
                        : ci === 0 && nameCatalog
                          ? <ComboInput value={row[c.key]} onChange={v => update(idx, c.key, v)} options={nameCatalog} />
                          : c.type === 'number'
                            ? <NumberInput value={row[c.key]} onChange={v => update(idx, c.key, v)} />
                            : c.type === 'date'
                              ? <input type="date" value={row[c.key] || ''} onChange={e => update(idx, c.key, e.target.value)} className={inputClass} />
                              : c.type === 'select'
                                ? <SelectInput value={row[c.key]} onChange={v => update(idx, c.key, v)} options={c.options || []} />
                                : c.type === 'combo'
                                  ? <ComboInput value={row[c.key]} onChange={v => update(idx, c.key, v)} options={c.options || []} placeholder={c.placeholder} />
                                  : c.type === 'multiselect'
                                    ? <MultiSelectInput value={row[c.key]} onChange={v => update(idx, c.key, v)} options={c.options || []} />
                                    : <TextInput value={row[c.key]} onChange={v => update(idx, c.key, v)} />}
                    </td>
                  );
                })}
                <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                  {idx !== nonRemovableIndex && <button type="button" onClick={() => remove(idx)} className="text-rose-500 text-xs hover:underline whitespace-nowrap">Remove</button>}
                </td>
              </tr>
            ))}
            {showTotals && (
              <tr className="bg-orange-500/5 dark:bg-orange-500/10">
                {columns.map((c, ci) => (
                  <td key={c.key} className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">
                    {ci === 0 ? 'Total' : c.noTotal ? '' : c.type === 'number' ? fmt(colTotal(c.key)) : c.type === 'computed' ? fmt(computedColTotal(c)) : ''}
                  </td>
                ))}
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-2.5">
        <AddButton onClick={add}>{addLabel.replace(/^\+\s*/, '')}</AddButton>
        {extraActions}
      </div>
    </div>
  );
}

// Same idea as ArrayEditor, but rows can carry an optional `group` name — a
// CA can tag any row into a group of their own naming (e.g. "Direct
// Manufacturing Cost", "Packaging Cost") right from the row itself, no
// separate "create group" step. With no row grouped, renders as one plain
// table (unchanged from ArrayEditor). The moment any row has a group, every
// distinct group gets its own collapsible section (chevron header, own
// mini-table, own subtotal); ungrouped rows still show in a plain table
// above them. Every operation (add/update/remove) works directly against
// the full underlying array by its real index, so there's no fragile
// virtual-subarray bookkeeping to keep in sync.
// The Group field commits on every keystroke like any other input — but
// GroupedArrayEditor re-partitions rows into collapsible sections the
// instant a row's group changes, which physically relocates that row's
// whole <tr> into a different table. Doing that live, keystroke-by-
// keystroke, yanks focus away after the very first character typed. This
// keeps the typed text in local, uncommitted state and only pushes it up
// (triggering the re-partition) on blur/Enter — so the row stays put,
// fully editable, until you're actually done naming the group. Existing
// group names are offered as click-to-apply chips instead of a dropdown,
// since a chip is a plain sibling element (no portal/blur race to manage)
// and commits immediately on click.
function GroupCell({ value, onCommit, options }) {
  const [draft, setDraft] = React.useState(value || '');
  React.useEffect(() => { setDraft(value || ''); }, [value]);
  const commit = () => { if (draft !== (value || '')) onCommit(draft); };
  const chips = options.filter(o => o !== draft);
  return (
    <div>
      <input
        className={inputClass}
        placeholder="Ungrouped — type to create a group…"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { commit(); e.currentTarget.blur(); } }}
      />
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {chips.map(o => (
            <button key={o} type="button" onMouseDown={e => e.preventDefault()} onClick={() => { setDraft(o); onCommit(o); }}
              className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500 hover:bg-orange-500/10 hover:text-[#ff5200] transition-colors">
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function GroupedArrayEditor({ rows, columns, onChange, addLabel = '+ Add Item', nameCatalog }) {
  const allRows = rows || [];
  const [openGroups, setOpenGroups] = React.useState({});

  const update = (idx, key, val) => {
    const next = allRows.slice();
    next[idx] = { ...next[idx], [key]: val };
    onChange(next);
  };
  const remove = (idx) => onChange(allRows.filter((_, i) => i !== idx));
  const addRow = () => onChange([...allRows, {}]);

  const groupNames = [];
  allRows.forEach(r => { if (r.group && !groupNames.includes(r.group)) groupNames.push(r.group); });
  const hasGroups = groupNames.length > 0;
  const fullColumns = [...columns, { key: 'group', label: 'Group', type: 'combo', options: groupNames, placeholder: 'Ungrouped — type to create a group…' }];

  const renderRows = (indices) => {
    const numericCols = fullColumns.filter(c => c.type === 'number' && !c.noTotal);
    const showTotals = indices.length > 1 && numericCols.length > 0;
    return (
      <div className="overflow-x-auto rounded-xl border border-slate-100 dark:border-slate-700 mb-2.5">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-900">
              {fullColumns.map(c => <th key={c.key} className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px]">{c.label}</th>)}
              <th className="border-b border-slate-200 dark:border-slate-700"></th>
            </tr>
          </thead>
          <tbody>
            {indices.map(idx => {
              const row = allRows[idx];
              return (
                <tr key={idx} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/30">
                  {fullColumns.map((c, ci) => (
                    <td key={c.key} className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                      {c.key === 'group'
                        ? <GroupCell value={row[c.key]} onCommit={v => update(idx, c.key, v)} options={c.options || []} />
                        : ci === 0 && nameCatalog
                          ? <ComboInput value={row[c.key]} onChange={v => update(idx, c.key, v)} options={nameCatalog} />
                          : c.type === 'number'
                            ? <NumberInput value={row[c.key]} onChange={v => update(idx, c.key, v)} />
                            : c.type === 'combo'
                              ? <ComboInput value={row[c.key]} onChange={v => update(idx, c.key, v)} options={c.options || []} placeholder={c.placeholder} />
                              : <TextInput value={row[c.key]} onChange={v => update(idx, c.key, v)} />}
                    </td>
                  ))}
                  <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800">
                    <button type="button" onClick={() => remove(idx)} className="text-rose-500 text-xs hover:underline whitespace-nowrap">Remove</button>
                  </td>
                </tr>
              );
            })}
            {showTotals && (
              <tr className="bg-orange-500/5 dark:bg-orange-500/10">
                {fullColumns.map((c, ci) => (
                  <td key={c.key} className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">
                    {ci === 0 ? 'Total' : (c.type === 'number' && !c.noTotal) ? fmt(indices.reduce((s, i) => s + (Number(allRows[i][c.key]) || 0), 0)) : ''}
                  </td>
                ))}
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  if (!hasGroups) {
    return (
      <div>
        {allRows.length > 0 && renderRows(allRows.map((_, i) => i))}
        <AddButton onClick={addRow} className="mt-2.5">{addLabel.replace(/^\+\s*/, '')}</AddButton>
      </div>
    );
  }

  const ungroupedIndices = allRows.map((_, i) => i).filter(i => !allRows[i].group);

  return (
    <div>
      {ungroupedIndices.length > 0 && renderRows(ungroupedIndices)}
      {groupNames.map(g => {
        const indices = allRows.map((_, i) => i).filter(i => allRows[i].group === g);
        const open = openGroups[g] ?? true;
        return (
          <div key={g} className="mb-3">
            <button type="button" onClick={() => setOpenGroups(o => ({ ...o, [g]: !open }))}
              className="flex items-center gap-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:text-[#ff5200] uppercase tracking-wide mb-1.5">
              <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"
                className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 8l4 4 4-4" />
              </svg>
              {g}
            </button>
            {open && renderRows(indices)}
          </div>
        );
      })}
      <AddButton onClick={addRow} className="mt-1">{addLabel.replace(/^\+\s*/, '')}</AddButton>
    </div>
  );
}

// Simple editable bullet list (SWOT points, assumptions) — each item a plain string.
export function StringListEditor({ items, onChange, placeholder = 'Add a point…' }) {
  const update = (idx, val) => { const next = (items || []).slice(); next[idx] = val; onChange(next); };
  const remove = (idx) => onChange((items || []).filter((_, i) => i !== idx));
  const add = () => onChange([...(items || []), '']);
  return (
    <div>
      {(items || []).map((item, idx) => (
        <div key={idx} className="flex items-center gap-2 mb-1.5">
          <span className="text-slate-300 text-xs flex-shrink-0">•</span>
          <input className="cx-input flex-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs text-slate-700 dark:text-slate-200 focus:outline-none focus:border-[#ff5200]"
            value={item} placeholder={placeholder} onChange={e => update(idx, e.target.value)} />
          <button type="button" onClick={() => remove(idx)} className="text-rose-400 hover:text-rose-500 text-xs flex-shrink-0">✕</button>
        </div>
      ))}
      <AddButton onClick={add} className="mt-1">Add point</AddButton>
    </div>
  );
}

export function TextArea({ value, onChange, rows = 4, placeholder }) {
  return (
    <textarea rows={rows} placeholder={placeholder}
      className="cx-input w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:border-[#ff5200] leading-relaxed"
      value={value || ''} onChange={e => onChange(e.target.value)} />
  );
}

const YEAR_TYPE_STYLE = {
  actual: { bg: 'bg-white dark:bg-slate-800', badge: 'slate', label: 'Actual' },
  estimated: { bg: 'bg-blue-50/60 dark:bg-blue-500/10', badge: 'indigo', label: 'Estimated' },
  projected: { bg: 'bg-blue-50/60 dark:bg-blue-500/10', badge: 'indigo', label: 'Projected' },
};

// Multi-year line-item entry grid — Operating Statement / Balance Sheet direct
// entry, matching the reference CMA tool's "type the actual/estimated/
// projected figures per line" workflow. White = Actual, blue tint =
// Estimated/Projected, custom rows appended below with their own delete.
export function FinancialGrid({ years, lines, values, customLines, onChangeValue, onChangeCustom, onAddCustom, onRemoveCustom, totals }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-900">
            <th className="text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 font-bold text-slate-500 uppercase tracking-wide text-[10px] sticky left-0 bg-slate-50 dark:bg-slate-900">Particulars</th>
            {years.map((y, i) => (
              <th key={i} className="text-right px-3 py-2 border-b border-slate-200 dark:border-slate-700 min-w-[110px]">
                <div className="font-bold text-slate-600 dark:text-slate-300">{y.label}</div>
                <Badge tone={YEAR_TYPE_STYLE[y.type]?.badge || 'slate'}>{YEAR_TYPE_STYLE[y.type]?.label || y.type}</Badge>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map(line => (
            <tr key={line.key}>
              <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-200 sticky left-0 bg-white dark:bg-slate-800 whitespace-nowrap">{line.label}</td>
              {years.map((y, i) => (
                <td key={i} className={`px-1.5 py-1 border-b border-slate-100 dark:border-slate-800 ${YEAR_TYPE_STYLE[y.type]?.bg || ''}`}>
                  <NumberInput value={(values[line.key] || [])[i]} onChange={v => onChangeValue(line.key, i, v)} />
                </td>
              ))}
            </tr>
          ))}
          {(customLines || []).map((line, ci) => (
            <tr key={`c${ci}`} className="bg-amber-50/40 dark:bg-amber-500/5">
              <td className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 sticky left-0 bg-amber-50/40 dark:bg-amber-500/5">
                <div className="flex items-center gap-1.5">
                  <TextInput value={line.label} onChange={v => onChangeCustom(ci, 'label', v)} placeholder="Custom line…" />
                  <button type="button" onClick={() => onRemoveCustom(ci)} className="text-rose-400 hover:text-rose-500 flex-shrink-0">✕</button>
                </div>
              </td>
              {years.map((y, i) => (
                <td key={i} className="px-1.5 py-1 border-b border-slate-100 dark:border-slate-800">
                  <NumberInput value={(line.values || [])[i]} onChange={v => { const next = (line.values || []).slice(); next[i] = v; onChangeCustom(ci, 'values', next); }} />
                </td>
              ))}
            </tr>
          ))}
          {totals && (
            <tr className="font-bold bg-slate-50 dark:bg-slate-900">
              <td className="px-3 py-2 sticky left-0 bg-slate-50 dark:bg-slate-900">{totals.label}</td>
              {years.map((y, i) => (
                <td key={i} className={`text-right px-3 py-2 ${totals.values[i] < 0 ? 'text-rose-500' : totals.values[i] === 0 ? 'text-emerald-600' : 'text-slate-700 dark:text-slate-200'}`}>
                  {fmt(totals.values[i])}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
      <AddButton onClick={onAddCustom} className="m-2">Add Custom Line</AddButton>
    </div>
  );
}

export function fmt(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// Indian-DPR-style Financial Year label ("2026-27") for a projection year
// index (1, 2, 3…), anchored to the Business Profile step's "Project Start
// Financial Year" — e.g. base 2026 + index 1 → "2026-27", index 2 →
// "2027-28". Falls back to the generic "Yr N" for any case that hasn't set
// a base year (or a plain index, when a caller wants that instead).
export function fyLabel(baseYear, yearIndex) {
  if (!baseYear) return `Yr ${yearIndex}`;
  const startYear = Number(baseYear) + (yearIndex - 1);
  const endYY = String((startYear + 1) % 100).padStart(2, '0');
  return `${startYear}-${endYY}`;
}

// ── Design-system primitives for the redesigned dashboard/wizard ──────────

// ChanakyaX brand tokens. Navy carries headings/text and the "final commit"
// button; orange is the energetic interactive accent — focus glow, active
// selection states, and the primary "start something new" CTA. Kept as
// plain constants (not a Tailwind theme extension) since this module reuses
// the CRA app's default Tailwind config as-is.
export const BRAND = {
  navy: '#001254', navyDark: '#000c3b', navyLight: '#1a3a6b',
  navyTint: '#f0f3fa', orange: '#ff5200', orangeHover: '#e04800',
  orangeTint: '#fff5f0', orangeBorder: 'rgba(255,82,0,0.25)',
};

export function Card({ children, className = '', onClick }) {
  return (
    <div onClick={onClick}
      className={`rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md hover:border-[#0f1f45]/20 dark:hover:border-orange-500/30 transition-all' : ''} ${className}`}>
      {children}
    </div>
  );
}

export function StatTile({ icon, label, value, accent = 'brand' }) {
  const accents = {
    brand: 'bg-[#0f1f45]/[0.06] text-[#0f1f45] dark:bg-orange-500/10 dark:text-orange-300',
    indigo: 'bg-[#0f1f45]/[0.06] text-[#0f1f45] dark:bg-orange-500/10 dark:text-orange-300',
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300',
    rose: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-300',
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  };
  return (
    <Card className="p-4 flex items-center gap-3.5">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${accents[accent]}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide truncate">{label}</div>
        <div className="text-xl font-extrabold text-slate-800 dark:text-slate-100 leading-tight">{value}</div>
      </div>
    </Card>
  );
}

export function Badge({ children, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    brand: 'bg-[#0f1f45]/[0.07] text-[#0f1f45] dark:bg-orange-500/15 dark:text-orange-300',
    indigo: 'bg-[#0f1f45]/[0.07] text-[#0f1f45] dark:bg-orange-500/15 dark:text-orange-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
    rose: 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold ${tones[tone]}`}>{children}</span>;
}

// variant="navy" (default) is the "commit/confirm" button — used for final
// submit actions like a modal's Create/Save. variant="orange" is the
// energetic "start something new" trigger — used for top-level + New
// Project style CTAs. Matches the two-button-color convention in the
// reference mockup (orange to open, navy to commit).
export function PrimaryButton({ children, onClick, disabled, type = 'button', className = '', variant = 'navy' }) {
  const gradient = variant === 'orange'
    ? `linear-gradient(135deg, ${BRAND.orange}, ${BRAND.orangeHover})`
    : `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.navyDark})`;
  const shadow = variant === 'orange' ? '0 4px 14px rgba(255,82,0,0.28)' : '0 4px 14px rgba(0,18,84,0.22)';
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      style={{ background: gradient, boxShadow: shadow }}
      className={`px-5 py-2.5 rounded-xl text-white text-sm font-bold hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 transition-all ${className}`}>
      {children}
    </button>
  );
}

// The wizard step footer's "Next" control — orange (the module's energetic
// "move forward" color, per the New Project modal convention), with the
// arrow icon sliding right on hover rather than the whole button lifting,
// for a subtler and more purposeful feel than a plain PrimaryButton. Paired
// with a navy PrimaryButton "Save" alongside it (save-in-place vs save-and-
// advance are two distinct actions, not one button doing both silently).
export function NextButton({ children, onClick, disabled, className = '' }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      style={{ background: `linear-gradient(135deg, ${BRAND.orange}, ${BRAND.orangeHover})`, boxShadow: '0 4px 14px rgba(255,82,0,0.28)' }}
      className={`group px-5 py-2.5 rounded-xl text-white text-sm font-bold hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-shadow inline-flex items-center gap-2 ${className}`}>
      <span>{children}</span>
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
        className="transition-transform duration-200 ease-out group-hover:translate-x-1">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
      </svg>
    </button>
  );
}

export function GhostButton({ children, onClick, className = '' }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${className}`}>
      {children}
    </button>
  );
}

// A benchmark badge for ratios (DSCR, Current Ratio, ...) — thresholds passed
// as an array of {min, tone, label} ordered ascending; picks the first match.
export function benchmarkTone(value, thresholds) {
  if (value === null || value === undefined || Number.isNaN(value)) return { tone: 'slate', label: '—' };
  const sorted = [...thresholds].sort((a, b) => a.min - b.min);
  let picked = sorted[0];
  for (const t of sorted) if (value >= t.min) picked = t;
  return picked;
}
